<template>
  <header class="logged-in-header">
    <div class="app-header-brand">
      <LoggedInNavigationMenu />
      <img class="app-header-logo" src="/assets/images/logo.svg" alt="SubAngstrom" />
    </div>

    <div class="app-header-spacer" aria-hidden="true" />

    <div class="app-header-actions">
      <span class="live-status" title="Inventory Yjs session">
        <span aria-hidden="true"></span>
        {{ syncLabel }}
      </span>

      <button
        type="button"
        class="icon-command top-message-button"
        aria-label="Open messages"
        title="Messages"
        @click="goToMessages"
      >
        <span class="material-symbols-outlined" aria-hidden="true">mail</span>
      </button>

      <div class="account-cluster">
        <div class="account-row">
          <span class="user-menu">Hi {{ firstName }}</span>
          <button type="button" class="logout-command" @click="store.dispatch('logout')">
            Logout
          </button>
        </div>
        <select
          v-if="availableLabOptions.length > 1"
          :value="activeLabId"
          class="lab-switch"
          aria-label="Switch active lab"
          :title="activeLabLabel"
          @change="handleLabChange"
        >
          <option v-for="lab in availableLabOptions" :key="lab.id" :value="lab.id">
            {{ lab.name }}
          </option>
        </select>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import store from "@/store/index.js";
import { useInventoryStore } from "@/stores/inventory";
import { useYjsProviderStore } from "@/stores/yjs-provider";
import LoggedInNavigationMenu from "@/components/ui/LoggedInNavigationMenu.vue";

const emit = defineEmits(["switch-lab"]);
const router = useRouter();
const inventoryStore = useInventoryStore();
const providerStore = useYjsProviderStore();

function canonicalId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.id || "";
}

const activeUser = computed(() => store.state.activeUser || {});
const firstName = computed(() => activeUser.value?.name?.split(" ")[0] || "there");
const activeLabId = computed(
  () => canonicalId(activeUser.value.activeLabId) || canonicalId(activeUser.value.labId)
);
const availableLabOptions = computed(() =>
  (activeUser.value.superLabIds || [])
    .filter((lab) => canonicalId(lab) && lab?.name)
    .map((lab) => ({ id: canonicalId(lab), name: String(lab.name) }))
);
const activeLabLabel = computed(() => {
  const selectedLab = availableLabOptions.value.find((lab) => lab.id === activeLabId.value);
  if (selectedLab) return selectedLab.name;
  return activeUser.value.activeLabId?.name || activeUser.value.labId?.name || "";
});
const syncLabel = computed(() => {
  if (providerStore.connectionStatus === "connected") return "Live";
  if (inventoryStore.isInitialized) return "Loaded";
  return "Syncing";
});

function handleLabChange(event) {
  emit("switch-lab", event.target.value);
}

function goToMessages() {
  if (router.currentRoute.value.name === "messages") return;
  router.push({ name: "messages" }).catch(() => {});
}
</script>

<style scoped>
.logged-in-header {
  position: sticky;
  top: 0;
  z-index: 51;
  display: grid;
  grid-template-columns: 142px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #d9e2ec;
  background: #fff;
}

.app-header-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.app-header-logo {
  display: block;
  width: min(134px, 100%);
  max-height: 38px;
  object-fit: contain;
}

.app-header-spacer {
  min-width: 0;
}

.app-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.icon-command,
.user-menu,
.lab-switch,
.logout-command,
.live-status {
  display: inline-flex;
  align-items: center;
  height: 30px;
  border: 1px solid #d6dde7;
  border-radius: 5px;
  background: #fff;
  color: #405065;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.icon-command,
.logout-command {
  justify-content: center;
  cursor: pointer;
}

.icon-command {
  width: 31px;
  margin: 0;
  padding: 0;
  color: #335c8b;
}

.icon-command:hover,
.icon-command:focus-visible,
.logout-command:hover,
.logout-command:focus-visible {
  border-color: #8fb0d4;
  background: #eef6ff;
  outline: 0;
}

.user-menu {
  padding: 0 8px;
  border-color: transparent;
  color: #334155;
}

.logout-command {
  width: auto;
  min-height: 30px;
  max-width: none;
  margin: 0;
  padding: 0 11px;
  color: #335c8b;
}

.lab-switch {
  min-width: 150px;
  max-width: 220px;
  padding: 0 26px 0 8px;
  border-color: #b8cadf;
  color: #1f334c;
  cursor: pointer;
}

.live-status {
  gap: 6px;
  padding: 0 9px;
  border-color: #bbd7c4;
  background: #f4fbf6;
  color: #155e2c;
}

.live-status span {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #16a34a;
}

.account-cluster {
  display: grid;
  justify-items: end;
  gap: 5px;
  min-width: 0;
}

.account-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

@media (max-width: 1180px) {
  .logged-in-header {
    grid-template-columns: 130px minmax(0, 1fr);
  }

  .app-header-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}

@media (max-width: 640px) {
  .logged-in-header {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 10px;
  }

  .app-header-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .user-menu {
    height: 28px;
    padding: 0 8px;
    font-size: 11px;
  }

  .account-cluster {
    justify-items: stretch;
    width: 100%;
  }

  .account-row {
    justify-content: flex-start;
  }

  .lab-switch {
    width: 100%;
    max-width: none;
  }
}
</style>
